/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useAuth } from "auth/Auth";
import EntityFormFields, { buildPayload, initialFormValues, missingRequired } from "./EntityFormFields";
import { apiErrorMessage } from "./listResponse";

/**
 * The generic add/edit dialog: renders form fields and POSTs them to `apiPath`
 * (or, with `record`, PUTs the changed fields to `apiPath/record.id`).
 * `defaults` pre-fills fields (a parent id when adding from a related list).
 * `onSubmit(payload)` replaces the request entirely for endpoints that are not
 * plain CRUD (price-list items are replaced as a whole list). onCreated
 * receives the response body.
 */
const EntityFormDialog = ({
  open,
  onClose,
  title,
  apiPath,
  fields,
  defaults,
  record,
  onSubmit,
  onCreated,
  submitLabel,
}) => {
  const { axiosInstance } = useAuth();
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setValues(initialFormValues(fields, record, defaults || {}));
      setError("");
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async () => {
    const missing = missingRequired(fields, values);
    if (missing.length) {
      setError(`Required: ${missing.join(", ")}`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      let res;
      if (record?.id) {
        const payload = buildPayload(fields, values, { onlyChangedFrom: record });
        res = onSubmit
          ? await onSubmit(payload, values)
          : await axiosInstance.put(`${apiPath}/${record.id}`, payload);
      } else {
        const payload = { ...buildPayload(fields, values) };
        Object.entries(defaults || {}).forEach(([k, v]) => {
          if (payload[k] === undefined || payload[k] === null) payload[k] = v;
        });
        res = onSubmit ? await onSubmit(payload, values) : await axiosInstance.post(apiPath, payload);
      }
      onCreated?.(res?.data);
      onClose();
    } catch (err) {
      console.error(`Error creating via ${apiPath}:`, err);
      setError(apiErrorMessage(err, "Could not save. Please check the fields and try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <EntityFormFields fields={fields} values={values} onChange={handleChange} disabled={saving} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{ color: "#fff" }}>
          {saving ? <CircularProgress size={18} color="inherit" /> : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EntityFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  apiPath: PropTypes.string,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  defaults: PropTypes.object,
  record: PropTypes.object,
  onSubmit: PropTypes.func,
  onCreated: PropTypes.func,
  submitLabel: PropTypes.string,
};

EntityFormDialog.defaultProps = {
  apiPath: "",
  defaults: null,
  record: null,
  onSubmit: null,
  onCreated: null,
  submitLabel: "Save",
};

export default EntityFormDialog;
